import {
  AgreeReactionIcon,
  CheckYesIcon,
  ClapReactionIcon,
  DisagreeReactionIcon,
  DislikeReactionIcon,
  HandRaiseIcon,
  LaughReactionIcon,
  LikeReactionIcon,
  LoveReactionIcon,
  WowReactionIcon,
  XNoIcon,
} from "@minervaproject/fds";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";

import { useIsFirstRender } from "hooks/use_is_first_render";
import { useUserProperties } from "hooks/use_user_properties";
import { SizeType } from "shared/types";

import "./signals_and_reactions.scss";

const COLOR_MAP = {
  like: "var(--green)",
  dislike: "var(--yellow)",
  agree: "var(--orange)",
  disagree: "var(--black-tint-40)",
  laugh: "var(--red-violet)",
  wow: "var(--blue)",
  love: "var(--red)",
  clap: "var(--blue-green)",
  hand: "var(--yellow-green-tint-20)",
};

const SIZE_MAP: Record<SizeType | number, number> = {
  xs: 12,
  sm: 16,
  md: 24,
  lg: 32,
  xl: 60,
};

type IconType = typeof LikeReactionIcon;
const REACTION_ICONS_MAP: { [key: string]: IconType } = {
  like: LikeReactionIcon,
  dislike: DislikeReactionIcon,
  agree: AgreeReactionIcon,
  disagree: DisagreeReactionIcon,
  laugh: LaughReactionIcon,
  wow: WowReactionIcon,
  love: LoveReactionIcon,
  clap: ClapReactionIcon,
};

const REACTION_INFO_MAP: {
  [key: string]: {
    color: string;
    color2?: string;
    ReactionIcon: IconType;
    shortcut: number;
  };
} = {
  like: { color: "green", ReactionIcon: REACTION_ICONS_MAP.like, shortcut: 1 },
  dislike: { color: "yellow", ReactionIcon: REACTION_ICONS_MAP.dislike, shortcut: 2 },
  agree: { color: "orange", ReactionIcon: REACTION_ICONS_MAP.agree, shortcut: 3 },
  disagree: {
    color: "black-tint-40",
    color2: "black-tint-70",
    ReactionIcon: REACTION_ICONS_MAP.disagree,
    shortcut: 4,
  },
  laugh: { color: "red-violet", ReactionIcon: REACTION_ICONS_MAP.laugh, shortcut: 5 },
  wow: { color: "blue", ReactionIcon: REACTION_ICONS_MAP.wow, shortcut: 6 },
  love: { color: "red", ReactionIcon: REACTION_ICONS_MAP.love, shortcut: 7 },
  clap: { color: "blue-green", ReactionIcon: REACTION_ICONS_MAP.clap, shortcut: 8 },
};

const VOTE_ICONS_MAP: { [key: string]: IconType } = {
  no: XNoIcon,
  yes: CheckYesIcon,
};

const REACTION_ANIMATION_DURATION = 2000;
const REMOVE_REACTION_DELAY = 395;

function SignalsAndReactions({
  userId,
  size = "lg",
  noAnimation = false,
}: {
  userId: string;
  size?: SizeType | number;
  noAnimation?: boolean;
}) {
  const isFirstRender = useIsFirstRender();
  const user = useUserProperties(userId, ["is_hand_raised", "reaction", "vote"]);

  const [currentReaction, setCurrentReaction] = useState(user?.reaction);
  const [isReactionAnimationVisible, setIsReactionAnimationVisible] = useState(false);
  const [isReactionIconVisible, setIsReactionIconVisible] = useState(!!user?.reaction);
  const [isReactionDisappearing, setIsReactionDisappearing] = useState(false);
  const reactionAnimationTimeout = useRef<NodeJS.Timeout>();
  const removeReactionTimeout = useRef<NodeJS.Timeout>();

  const [isHandRaiseAnimationVisible, setIsHandRaiseAnimationVisible] = useState(false);
  const [isHandRaiseIconVisible, setIsHandRaiseIconVisible] = useState(user?.is_hand_raised);
  const [isHandRaiseDisappearing, setIsHandRaiseDisappearing] = useState(false);
  const handRaiseAnimationTimeout = useRef<NodeJS.Timeout>();
  const removeHandRaiseTimeout = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (isFirstRender) {
      // Hack to prevent animation from showing on first render
      return;
    }

    if (user?.reaction) {
      if (reactionAnimationTimeout.current) {
        setIsReactionAnimationVisible(false);
        setIsReactionIconVisible(false);
        clearTimeout(reactionAnimationTimeout.current);
      }
      if (removeReactionTimeout.current) {
        clearTimeout(removeReactionTimeout.current);
      }

      // This timeout of 0 is needed so that the previous reaction is cleared first
      setTimeout(() => {
        setCurrentReaction(user.reaction);
        setIsReactionAnimationVisible(true);
        setIsReactionDisappearing(false);
        reactionAnimationTimeout.current = setTimeout(() => {
          setIsReactionAnimationVisible(false);
          setIsReactionIconVisible(true);
        }, REACTION_ANIMATION_DURATION);
      }, 0);
    } else if (user?.reaction === null) {
      setIsReactionDisappearing(true);
      setIsReactionIconVisible(false);
      removeReactionTimeout.current = setTimeout(() => {
        setCurrentReaction(null);
        setIsReactionDisappearing(false);
      }, REMOVE_REACTION_DELAY); // after animations are complete
    }

    return () => {
      if (reactionAnimationTimeout.current) {
        clearTimeout(reactionAnimationTimeout.current);
      }
      if (removeReactionTimeout.current) {
        clearTimeout(removeReactionTimeout.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.reaction]);

  useEffect(() => {
    if (isFirstRender) {
      // Hack to prevent animation from showing on first render
      return;
    }

    if (user?.is_hand_raised) {
      if (handRaiseAnimationTimeout.current) {
        setIsHandRaiseAnimationVisible(false);
        setIsHandRaiseIconVisible(false);
        clearTimeout(handRaiseAnimationTimeout.current);
      }
      if (removeHandRaiseTimeout.current) {
        clearTimeout(removeHandRaiseTimeout.current);
      }

      // This timeout of 0 is needed so that the previous hand raise is cleared first
      setTimeout(() => {
        setIsHandRaiseAnimationVisible(true);
        setIsHandRaiseDisappearing(false);
        handRaiseAnimationTimeout.current = setTimeout(() => {
          setIsHandRaiseAnimationVisible(false);
          setIsHandRaiseIconVisible(true);
        }, REACTION_ANIMATION_DURATION);
      }, 0);
    } else if (user?.is_hand_raised === false) {
      setIsHandRaiseDisappearing(true);
      setIsHandRaiseIconVisible(false);
      removeHandRaiseTimeout.current = setTimeout(() => {
        setIsHandRaiseDisappearing(false);
      }, REMOVE_REACTION_DELAY); // after animations are complete
    }

    return () => {
      if (handRaiseAnimationTimeout.current) {
        clearTimeout(handRaiseAnimationTimeout.current);
      }
      if (removeHandRaiseTimeout.current) {
        clearTimeout(removeHandRaiseTimeout.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.is_hand_raised]);

  const voteIconArgs = {
    className: "absolute appearing",
    width: SIZE_MAP[size] || size,
    height: SIZE_MAP[size] || size,
  };

  const getReactionIconArgs = () => {
    return {
      className: clsx("absolute fade-transition", {
        appearing: isReactionIconVisible,
        disappearing: isReactionDisappearing,
        faded: isReactionDisappearing,
      }),
      width: SIZE_MAP[size] || size,
      height: SIZE_MAP[size] || size,
    };
  };

  const getHandRaiseIconArgs = () => {
    return {
      className: clsx("absolute fade-transition", {
        appearing: isHandRaiseIconVisible,
        disappearing: isHandRaiseDisappearing,
        faded: isHandRaiseDisappearing,
      }),
      width: SIZE_MAP[size] || size,
      height: SIZE_MAP[size] || size,
    };
  };

  const ReactionIcon = currentReaction && REACTION_ICONS_MAP[currentReaction];
  const VoteIcon = user?.vote && VOTE_ICONS_MAP[user?.vote];

  if (!user) {
    return null;
  }

  return (
    <div className="absolute top-0 left-0 w-full h-full overflow-hidden">
      <div className="absolute top-0 left-0 m1 user-signals">
        {(isReactionIconVisible || isReactionDisappearing) && currentReaction && ReactionIcon && (
          <ReactionIcon
            {...getReactionIconArgs()}
            data-testid={`reaction-${user.reaction}-${user.id}`}
          />
        )}
        {user.vote && VoteIcon && (
          <VoteIcon {...voteIconArgs} data-testid={`vote-${user.vote}-${user.id}`} />
        )}
        {(isHandRaiseIconVisible || isHandRaiseDisappearing) && (
          <HandRaiseIcon {...getHandRaiseIconArgs()} data-testid={`hand-raise-${user.id}`} />
        )}
      </div>
      {!noAnimation && isReactionAnimationVisible && currentReaction && (
        <div
          className={clsx("emotion-signals", currentReaction)}
          data-testid={`reaction-${user.reaction}-${user.id}`}
        >
          <div className="circle-fill">
            <svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
              <circle cx="50%" cy="50%" r="50%" fill={COLOR_MAP[currentReaction]} />
            </svg>
          </div>
          {currentReaction && ReactionIcon && (
            <div className="emotion-icon">
              <ReactionIcon color="transparent" />
            </div>
          )}
        </div>
      )}
      {!noAnimation && isHandRaiseAnimationVisible && (
        <div className="emotion-signals hand-raise" data-testid={`hand-raise-${user.id}`}>
          <div className="circle-fill">
            <svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
              <circle cx="50%" cy="50%" r="50%" fill={COLOR_MAP.hand} />
            </svg>
          </div>
          <div className="emotion-icon">
            <HandRaiseIcon color="transparent" />
          </div>
        </div>
      )}
    </div>
  );
}

export { REACTION_ICONS_MAP, REACTION_INFO_MAP, VOTE_ICONS_MAP, SignalsAndReactions };
